import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useAuth } from "../../sdk";

const initalRatePlanData = {
  roomRateGroupCode: "",
  roomRatePlanName: "",
  promotionalDiscount: null,
  occupancyType: "",
  isBaseRoom: false,
};

const initialNewRoomData = {
  roomId: "",
  roomName: "",
  ...initalRatePlanData,
};

function transformRoomsData(roomsData = []) {
  if (!roomsData || roomsData.length === 0) {
    return [];
  }

  const groupedByRoom = roomsData.reduce((acc, item) => {
    const room = acc[item.roomId] || {
      roomId: item.roomId,
      roomName: item.roomName,
      externalSequence: item.externalSequence,
      propertyId: item.propertyId,
      ratePlans: [],
    };

    room.ratePlans.push({ ...item, isEditRatePlan: false });
    acc[item.roomId] = room;
    return acc;
  }, {});

  return Object.values(groupedByRoom)
    .map((room) => ({
      ...room,
      ratePlans: room.ratePlans.sort((a, b) => a.sequence - b.sequence),
    }))
    .sort((a, b) => a.externalSequence - b.externalSequence);
}

export function useResavenueRooms() {
  const { authFetch } = useAuth();
  const { hotelId } = useParams();
  const [roomsData, setRoomsData] = useState([]);
  const [isFetchingRooms, setIsFetchingRooms] = useState(true);
  const [addNewRoom, setAddNewRoom] = useState(false);
  const [addNewRoomData, setAddNewRoomData] = useState(initialNewRoomData);
  const [addNewRatePlan, setAddNewRatePlan] = useState(null);
  const [newRatePlanData, setNewRatePlanData] = useState(initalRatePlanData);
  const [snackbarMsg, setSnackBarMsg] = useState({
    open: false,
    isError: false,
    message: "",
  });
  const [totalSequence, setTotalSequence] = useState(null);

  // Fetch Resavenue Room Mapping
  const fetchRooms = useCallback(async () => {
    if (!hotelId) return;
    try {
      setIsFetchingRooms(true);
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/get-room-rate-plan`,
      });

      const { data, response } = await get();
      if (data && response?.ok) {
        setTotalSequence(data?.length);
        const transformedData = transformRoomsData(data);
        setRoomsData(transformedData);
      }
    } catch (err) {
      console.error("Error fetching rooms mapping", err);
    } finally {
      setIsFetchingRooms(false);
    }
  }, [authFetch, hotelId]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // Update Room Sequence
  const changeExternalSequence = useCallback(
    async (formerSequence, laterSequence) => {
      if (!hotelId) return;
      const payload = { formerSequence, laterSequence };
      try {
        const { put } = await authFetch({
          path: `/hotel/${hotelId}/room-rate-plan/external-sequence`,
        });

        const { response } = await put(payload);

        if (response?.ok) {
          fetchRooms();
          setSnackBarMsg({
            open: true,
            isError: false,
            message:
              response?.data?.type || "Successfully updated External Sequence",
          });
        } else {
          fetchRooms();
          setSnackBarMsg({
            open: true,
            isError: true,
            message:
              response?.data?.messageToUser ||
              "Error Updating External Sequence",
          });
        }
      } catch (err) {
        fetchRooms();
        console.error("Error updating external sequence", err);
        setSnackBarMsg({
          open: true,
          isError: true,
          message: "Something Went Wrong",
        });
      }
    },
    [authFetch, fetchRooms, hotelId]
  );

  // Update Rateplan Sequence
  const changeRatePlanSequence = useCallback(
    async (ratePlanId, formerSequence, laterSequence) => {
      if (!hotelId) return;

      const payload = {
        formerId: ratePlanId,
        formerSequence,
        laterSequence,
      };

      try {
        const { post } = await authFetch({
          path: `/hotel/${hotelId}/room-rate-plan/sequence`,
        });

        const { response } = await post(payload);

        if (response?.ok) {
          fetchRooms();
          setSnackBarMsg({
            open: true,
            isError: false,
            message:
              response?.data?.messageToUser ||
              "Successfully updated Internal Sequence",
          });
        } else {
          fetchRooms();
          setSnackBarMsg({
            open: true,
            isError: true,
            message:
              response?.data?.messageToUser ||
              "Error Updating Internal Sequence",
          });
        }
      } catch (error) {
        fetchRooms();
        console.error("Error Updating Rateplan Sequence", error);
        setSnackBarMsg({
          open: true,
          isError: true,
          message: "Something Went Wrong!",
        });
      }
    },
    [authFetch, fetchRooms, hotelId]
  );

  // Update RatePlan Data
  const updateRatePlan = useCallback(
    async (payload, roomIndex, ratePlanIndex) => {
      if (!hotelId) return;
      const ratePlanId = payload?.RoomRatePlanId;
      try {
        const { put } = await authFetch({
          path: `/hotel/${hotelId}/update-room-rate-plan?ratePlanId=${ratePlanId}`,
        });

        const { response } = await put(payload);

        if (response?.ok) {
          handleEditRateplan(roomIndex, ratePlanIndex);
          setSnackBarMsg({
            open: true,
            isError: false,
            message:
              response?.data?.type || "Successfully Updated rateplan data",
          });
        } else {
          fetchRooms();
          setSnackBarMsg({
            open: true,
            isError: true,
            message:
              response?.data?.messageToUser || "Error updating rateplan rata",
          });
        }
      } catch (error) {
        fetchRooms();
        console.error("Error updating rateplan data", error);
        setSnackBarMsg({
          open: true,
          isError: true,
          message: "Something Went Wrong!",
        });
      }
    },
    [authFetch, fetchRooms, hotelId]
  );

  const createRoomOrRatePlan = useCallback(
    async (isCreateRoom, room, newRatePlanData) => {
      if (!hotelId) return;
      const { roomId, roomName, externalSequence, propertyId } = room;
      const payload = isCreateRoom
        ? {
            externalSequence: roomsData?.length + 1,
            sequence: totalSequence + 1,
            ...room,
          }
        : {
            roomId,
            roomName,
            propertyId,
            sequence: totalSequence + 1,
            externalSequence,
            ...newRatePlanData,
          };
      try {
        const { post } = await authFetch({
          path: `/hotel/${hotelId}/create-room-rate-plan`,
        });
        const { response } = await post(payload);
        if (response?.ok) {
          fetchRooms();
          if (isCreateRoom) {
            setAddNewRoom(false);
            setAddNewRoomData(initialNewRoomData);
          } else {
            setAddNewRatePlan(null);
            setNewRatePlanData(initalRatePlanData);
          }
          setSnackBarMsg({
            open: true,
            isError: false,
            message:
              response?.data?.type || "Successfully created rateplan data",
          });
        } else {
          setSnackBarMsg({
            open: true,
            isError: true,
            message:
              response?.data?.messageToUser || "Error updating rateplan data",
          });
        }
      } catch (error) {
        console.error("Error creating room rateplan", error);
        setSnackBarMsg({
          open: true,
          isError: true,
          message: "Something Went Wrong!",
        });
      }
    },
    [authFetch, fetchRooms, hotelId, roomsData.length, totalSequence]
  );

  // Toggle edit mode
  const handleEditRateplan = (roomIndex, ratePlanIndex) => {
    setRoomsData((prev) =>
      prev.map((r, rIndex) =>
        rIndex === roomIndex
          ? {
              ...r,
              ratePlans: r.ratePlans.map((rp, rpIndex) =>
                rpIndex === ratePlanIndex
                  ? { ...rp, isEditRatePlan: !rp.isEditRatePlan }
                  : rp
              ),
            }
          : r
      )
    );
  };

  // Update Rateplan Change Handler
  const changeHandlerRatePlan = (roomIndex, ratePlanIndex, key, value) => {
    setRoomsData((prev) =>
      prev.map((room, rIndex) =>
        rIndex === roomIndex
          ? {
              ...room,
              ratePlans: room.ratePlans.map((ratePlan, rpIndex) =>
                rpIndex === ratePlanIndex
                  ? { ...ratePlan, [key]: value }
                  : ratePlan
              ),
            }
          : room
      )
    );
  };

  // Handle input changes in "Add New Room"
  const handleNewRoomChange = (key, value) => {
    setAddNewRoomData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Handle changes in Add New Rate Plan fields
  const handleNewRatePlanChange = (key, value) => {
    setNewRatePlanData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // State Fields Validator
  const isDataIncomplete = (data, template) => {
    return Object.keys(template)
      .filter((item) => item !== "promotionalDiscount")
      .some((key) => {
        const value = data[key];
        return value === undefined || value === null || value === "";
      });
  };

  // Check for all fields of New Room
  const isRoomDataIncomplete = isDataIncomplete(
    addNewRoomData,
    initialNewRoomData
  );

  // Check for all fields of new Rate Plan
  const isRatePlanDataIncomplete = isDataIncomplete(
    newRatePlanData,
    initalRatePlanData
  );

  return {
    roomsData,
    setRoomsData,
    isFetchingRooms,
    snackbarMsg,
    setSnackBarMsg,
    addNewRoom,
    addNewRoomData,
    setAddNewRoom,
    handleNewRoomChange,
    isRoomDataIncomplete,
    addNewRatePlan,
    newRatePlanData,
    setAddNewRatePlan,
    handleNewRatePlanChange,
    isRatePlanDataIncomplete,
    createRoomOrRatePlan,
    updateRatePlan,
    handleEditRateplan,
    changeHandlerRatePlan,
    changeExternalSequence,
    changeRatePlanSequence,
    fetchRooms,
  };
}
